/**
 * SkerpaMessageContent — Markdown renderer, action cards, and suggestion pills.
 * Extracted from HermesChatPanel to keep files under 300 lines.
 */

import { useState } from 'react'
import { Bot, Plus, Check, Loader2, ArrowRight, MessageCircleQuestion, Pencil, ChevronRight, ChevronDown } from 'lucide-react'
import { inlineMd } from '@/components/dual-space/coachFormatting'
import type { HermesChatMessage, SkerpaAction } from '@ispot/shared'

// ============================================================================
// MESSAGE BUBBLE
// ============================================================================

export function MessageBubble({ message }: { message: HermesChatMessage }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-warm-100 text-text rounded-xl rounded-tr-sm px-3 py-1.5">
          <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-1.5">
      <div className="w-5 h-5 rounded-full bg-solution/15 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Bot className="w-3 h-3 text-solution-700" />
      </div>
      <div className="flex-1 min-w-0">
        <MarkdownContent content={message.content} />
      </div>
    </div>
  )
}

// ============================================================================
// ACTION CARDS
// ============================================================================

interface ActionCardsProps {
  actions: SkerpaAction[]
  executedActions: Set<string>
  executingAction: string | null
  onExecute: (action: SkerpaAction, key: string) => void
  onStartInterview?: (questions: string[]) => void
}

/** Map section IDs to human-readable names */
const SECTION_LABELS: Record<string, string> = {
  'key-partners': 'Key Partners', 'key-activities': 'Key Activities', 'key-resources': 'Key Resources',
  'value-propositions': 'Value Propositions', 'customer-relationships': 'Customer Relationships',
  'channels': 'Channels', 'customer-segments': 'Customer Segments',
  'cost-structure': 'Cost Structure', 'revenue-streams': 'Revenue Streams',
  'customer-jobs': 'Customer Jobs', 'customer-gains': 'Gains', 'customer-pains': 'Pains',
  'products-services': 'Products & Services', 'pain-relievers': 'Pain Relievers',
  'gain-creators': 'Gain Creators',
  'goals': 'Goals', 'pains': 'Pain Points', 'behaviors': 'Behaviors',
  'target-customer': 'Target Customer', 'competitors': 'Competitors',
  'unique-value': 'Unique Value', 'key-benefit': 'Key Benefit',
  'proof-points': 'Proof Points', 'tagline': 'Tagline',
  'context': 'Context', 'problem': 'Problem', 'alternatives': 'Alternatives',
  'customers': 'Customers', 'emotional-impact': 'Emotional Impact',
  'quantifiable-impact': 'Quantifiable Impact',
  'alternative-shortcomings': 'Alternative Shortcomings',
}

export function ActionCards({ actions, executedActions, executingAction, onExecute, onStartInterview }: ActionCardsProps) {
  // Group add_entity actions by section; update_entity and others kept separate
  const entityActions = actions.filter(a => a.action === 'add_entity') as Array<Extract<SkerpaAction, { action: 'add_entity' }>>
  const updateActions = actions.filter(a => a.action === 'update_entity') as Array<Extract<SkerpaAction, { action: 'update_entity' }>>
  const batchActions = actions.filter(a => a.action === 'batch_update') as Array<Extract<SkerpaAction, { action: 'batch_update' }>>
  const otherActions = actions.filter(a => a.action !== 'add_entity' && a.action !== 'update_entity' && a.action !== 'batch_update')

  // Group by section
  const groups = new Map<string, Array<{ action: Extract<SkerpaAction, { action: 'add_entity' }>; index: number }>>()
  entityActions.forEach((action, i) => {
    const section = action.section || 'general'
    if (!groups.has(section)) groups.set(section, [])
    groups.get(section)!.push({ action, index: actions.indexOf(action) })
  })

  return (
    <div className="ml-8 mt-1.5 space-y-1.5">
      {/* Entity actions grouped by section */}
      {Array.from(groups.entries()).map(([section, items]) => (
        <div key={section}>
          <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wide mb-1 px-1">
            {SECTION_LABELS[section] || section.replace(/-/g, ' ')}
          </p>
          <div className="space-y-0.5">
            {items.map(({ action, index }) => {
              const key = `add_entity-${action.label}-${index}`
              const isDone = executedActions.has(key)
              const isRunning = executingAction === key
              return (
                <div
                  key={key}
                  className={`flex items-center gap-2 border-l-2 pl-2.5 pr-2 py-1.5 rounded-r text-[12px] transition-colors ${
                    isDone ? 'border-l-market/40 text-text-muted' : 'border-l-market/60 hover:bg-warm-50'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-text">{action.label}</span>
                    {action.description && <span className="text-text-muted ml-1.5">{action.description}</span>}
                  </div>
                  <button
                    onClick={() => onExecute(action, key)}
                    disabled={isDone || isRunning}
                    title={isDone ? 'Added' : `Add ${action.label}`}
                    aria-label={isDone ? `Added ${action.label}` : `Add ${action.label}`}
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                      isDone ? 'text-market' : isRunning ? 'text-warm-400' : 'text-market/60 hover:text-market'
                    }`}
                  >
                    {isDone ? <Check className="w-3 h-3" /> : isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {/* Batch update actions — single button for bulk operations */}
      {batchActions.map((action, i) => {
        const key = `batch_update-${action.entityType}-${i}`
        const isDone = executedActions.has(key)
        const isRunning = executingAction === key
        return (
          <button key={key} onClick={() => onExecute(action, key)} disabled={isDone || isRunning}
            title={isDone ? 'Changes applied' : `Apply ${action.items.length} changes`}
            className={`flex items-center gap-2 border-l-2 pl-2.5 pr-2 py-2 rounded-r text-[12px] transition-colors w-full text-left ${isDone ? 'border-l-business/40' : 'border-l-business/60 hover:bg-warm-50'}`}>
            {isDone ? <Check className="w-3.5 h-3.5 text-business flex-shrink-0" /> : isRunning ? <Loader2 className="w-3.5 h-3.5 text-business animate-spin flex-shrink-0" /> : <Pencil className="w-3.5 h-3.5 text-business/60 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <span className="font-medium text-text">{action.description || `Update ${action.items.length} ${action.entityType.toLowerCase()}s`}</span>
              <span className="text-text-muted ml-1.5">({action.items.length} changes)</span>
            </div>
          </button>
        )
      })}

      {/* Update entity actions */}
      {updateActions.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-text-muted uppercase tracking-wide mb-1 px-1">Updates</p>
          <div className="space-y-0.5">
            {updateActions.map((action, i) => {
              const key = `update_entity-${action.name}-${i}`
              const isDone = executedActions.has(key)
              const isRunning = executingAction === key
              const changeDesc = Object.entries(action.updates).map(([k, v]) => `${k}: ${v}`).join(', ')
              return (
                <div key={key} className={`flex items-center gap-2 border-l-2 pl-2.5 pr-2 py-1.5 rounded-r text-[12px] transition-colors ${isDone ? 'border-l-business/40 text-text-muted' : 'border-l-business/60 hover:bg-warm-50'}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-text">{action.name}</span>
                    <span className="text-text-muted ml-1.5">{changeDesc}</span>
                  </div>
                  <button onClick={() => onExecute(action, key)} disabled={isDone || isRunning}
                    title={isDone ? 'Update applied' : `Update ${action.name}`}
                    aria-label={isDone ? `Updated ${action.name}` : `Update ${action.name}`}
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${isDone ? 'text-business' : isRunning ? 'text-warm-400' : 'text-business/60 hover:text-business'}`}>
                    {isDone ? <Check className="w-3 h-3" /> : isRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pencil className="w-2.5 h-2.5" />}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Other actions (navigate, interview) */}
      {otherActions.map((action, i) => {
        const key = `${action.action}-${'target' in action ? action.target : 'interview'}-${i}`

        if (action.action === 'navigate') {
          return (
            <button key={key} onClick={() => onExecute(action, key)}
              className="flex items-center gap-2 border-l-2 border-l-warm-300 pl-2.5 pr-2 py-1.5 rounded-r text-[12px] transition-colors w-full text-left hover:bg-warm-50">
              <ArrowRight className="w-3 h-3 text-text-muted flex-shrink-0" />
              <span className="font-medium text-text">{action.label || action.target}</span>
            </button>
          )
        }

        if (action.action === 'interview' && onStartInterview) {
          return (
            <button key={key} onClick={() => onStartInterview(action.questions)}
              className="flex items-center gap-2 border-l-2 border-l-business pl-2.5 pr-2 py-2 rounded-r text-[12px] transition-colors w-full text-left hover:bg-warm-50">
              <MessageCircleQuestion className="w-3.5 h-3.5 text-business flex-shrink-0" />
              <div>
                <span className="font-medium text-text">Answer one by one</span>
                <span className="text-text-muted ml-1.5">{action.questions.length} questions</span>
              </div>
            </button>
          )
        }
        return null
      })}
    </div>
  )
}

// ============================================================================
// QUESTION EXTRACTION — detects multi-question responses for interview mode
// ============================================================================

export function extractQuestions(content: string): string[] {
  const questions: string[] = []
  const lines = content.split('\n')
  for (const line of lines) {
    const trimmed = line.trim()
    // Match: "1. Question?" or "**Question?**" or "› Question?"
    const match = trimmed.match(/^(?:\d+\.\s+|\*\*|›\s+)(.+\?)\s*\**$/)
    if (match) {
      questions.push(match[1].replace(/\*\*/g, '').trim())
    }
  }
  return questions.length >= 2 ? questions : []
}

// ============================================================================
// SUGGESTION PILLS
// ============================================================================

export function extractPills(content: string): { label: string; prompt: string }[] {
  const pills: { label: string; prompt: string }[] = []
  const lines = content.split('\n')
  for (const line of lines) {
    // Explicit choice pills: ">> Pick the weakest persona", ">> Zoom"
    // Allow short labels (entity names like "Zoom", "Loom", "Asana") and long
    // ones (full pain statements). Any non-empty content after `>> ` qualifies.
    const choiceMatch = line.match(/^>>\s+(.{1,200})$/)
    if (choiceMatch) {
      pills.push({ label: choiceMatch[1].replace(/\*\*/g, '').trim(), prompt: choiceMatch[1].replace(/\*\*/g, '').trim() })
      continue
    }
    // Action-verb bullets
    const match = line.match(/^[-•›]\s+(.{10,80})$/)
    if (!match) continue
    const text = match[1].replace(/\*\*/g, '').trim()
    if (/^(Review|Add|Create|Validate|Analyze|Define|Update|Check|Explore|Refine|Map|Build|Compare|Identify|Research|Document|Test|Prioritize|Tell|Pick|Focus|Help|Show|Suggest)\b/i.test(text)) {
      pills.push({ label: text, prompt: text })
    }
  }
  return pills
}

// ============================================================================
// MARKDOWN CONTENT
// ============================================================================

export function MarkdownContent({ content }: { content: string }) {
  const elements = parseToElements(content)

  return (
    <div className="text-[12.5px] text-text leading-relaxed space-y-1">
      {elements.map((el, i) => {
        switch (el.type) {
          case 'heading':
            return <p key={i} className="font-semibold text-text mt-2 first:mt-0 text-[12.5px]"><span dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} /></p>
          case 'bullet':
            return (
              <div key={i} className="flex gap-1.5 pl-0.5">
                <span className="text-warm-400 flex-shrink-0 leading-relaxed">›</span>
                <span className="break-words" dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} />
              </div>
            )
          case 'collapsible':
            return <CollapsibleBlock key={i} title={el.text} children={el.children || []} />
          case 'blockquote':
            return <div key={i} className="border-l-2 border-business/30 pl-3 text-text-secondary italic" dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} />
          case 'hr':
            return <hr key={i} className="border-border-subtle my-2" />
          case 'table':
            return <MarkdownTable key={i} rows={el.rows!} />
          default:
            return <p key={i} className="break-words"><span dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} /></p>
        }
      })}
    </div>
  )
}

// ── Collapsible block ──

function CollapsibleBlock({ title, children }: { title: string; children: Element[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-l-2 border-l-warm-200 rounded-r">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 w-full text-left pl-2.5 pr-2 py-1.5 text-[12px] text-text-secondary hover:bg-warm-50 transition-colors rounded-r"
      >
        {open ? <ChevronDown className="w-3 h-3 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 flex-shrink-0" />}
        <span dangerouslySetInnerHTML={{ __html: inlineMd(title) }} />
      </button>
      {open && (
        <div className="pl-2.5 pr-2 pb-2 space-y-1 text-[12px] text-text-secondary">
          {children.map((el, i) => {
            switch (el.type) {
              case 'bullet':
                return (
                  <div key={i} className="flex gap-1.5 pl-1">
                    <span className="text-warm-400 flex-shrink-0">›</span>
                    <span className="break-words" dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} />
                  </div>
                )
              default:
                return <p key={i} className="break-words" dangerouslySetInnerHTML={{ __html: inlineMd(el.text) }} />
            }
          })}
        </div>
      )}
    </div>
  )
}

// ── Internal helpers ──

interface Element {
  type: 'heading' | 'bullet' | 'paragraph' | 'blockquote' | 'hr' | 'table' | 'collapsible'
  text: string
  rows?: string[][]
  children?: Element[]  // nested elements inside collapsible
}

function parseToElements(content: string): Element[] {
  const lines = content.split('\n')
  const elements: Element[] = []
  let i = 0

  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (!trimmed) { i++; continue }
    // Skip choice pill lines (rendered as clickable chips, not text)
    if (trimmed.startsWith('>> ')) { i++; continue }
    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) { elements.push({ type: 'hr', text: '' }); i++; continue }
    // Collapsible <details> blocks
    if (trimmed.startsWith('<details>') || trimmed.startsWith('<details ')) {
      let summary = ''
      // Check for <summary> on same line or next lines
      const summaryInline = trimmed.match(/<summary>(.+?)<\/summary>/)
      if (summaryInline) {
        summary = summaryInline[1].trim()
      }
      i++
      // If summary wasn't inline, check next line
      if (!summary && i < lines.length) {
        const nextTrimmed = lines[i].trim()
        const summaryMatch = nextTrimmed.match(/<summary>(.+?)<\/summary>/)
        if (summaryMatch) {
          summary = summaryMatch[1].trim()
          i++
        }
      }
      // Collect inner content until </details>
      const innerLines: string[] = []
      while (i < lines.length && !lines[i].trim().startsWith('</details>')) {
        innerLines.push(lines[i])
        i++
      }
      if (i < lines.length) i++ // skip </details>
      const children = parseToElements(innerLines.join('\n'))
      elements.push({ type: 'collapsible', text: summary || 'Details', children })
      continue
    }
    // Skip stray summary/details tags
    if (trimmed.startsWith('<summary>') || trimmed.startsWith('</details>') || trimmed.startsWith('</summary>')) { i++; continue }
    if (trimmed.startsWith('|')) {
      const tableLines: string[] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) { tableLines.push(lines[i].trim()); i++ }
      const rows = parseTableLines(tableLines)
      if (rows.length > 0) elements.push({ type: 'table', text: '', rows })
      continue
    }
    if (/^#{1,3}\s/.test(trimmed) || /^\*\*[^*]+:\*\*$/.test(trimmed)) {
      elements.push({ type: 'heading', text: trimmed.replace(/^#{1,3}\s/, '').replace(/^\*\*/, '').replace(/:\*\*$/, '') }); i++; continue
    }
    if (trimmed.startsWith('>')) { elements.push({ type: 'blockquote', text: trimmed.replace(/^>\s?/, '') }); i++; continue }
    if (/^[-•→›]\s/.test(trimmed) || /^\d+\.\s/.test(trimmed)) {
      elements.push({ type: 'bullet', text: trimmed.replace(/^[-•→›]\s/, '').replace(/^\d+\.\s/, '') }); i++; continue
    }
    elements.push({ type: 'paragraph', text: trimmed }); i++
  }
  return elements
}

function parseTableLines(lines: string[]): string[][] {
  return lines
    .filter(l => !l.match(/^\|[\s-:|]+\|$/))
    .map(l => l.split('|').slice(1, -1).map(cell => cell.trim()))
    .filter(row => row.length > 0 && row.some(cell => cell.length > 0))
}

function MarkdownTable({ rows }: { rows: string[][] }) {
  if (rows.length === 0) return null
  const [header, ...body] = rows
  return (
    <div className="overflow-x-auto my-2">
      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr>
            {header.map((cell, j) => (
              <th key={j} className="text-left font-semibold text-text-secondary px-2.5 py-1.5 bg-warm-100 border-b border-border-subtle first:rounded-tl last:rounded-tr">
                <span dangerouslySetInnerHTML={{ __html: inlineMd(cell) }} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? '' : 'bg-warm-50/50'}>
              {row.map((cell, j) => (
                <td key={j} className="px-2.5 py-1.5 border-b border-border-subtle/50 text-text-secondary">
                  <span dangerouslySetInnerHTML={{ __html: inlineMd(cell) }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
